import React, { useCallback } from 'react';
import styled from '@xstyled/styled-components';
import Markdown from 'markdown-to-jsx';
import DOMPurify from 'dompurify';

const MessageItem = styled.li<{
  type: 'incoming' | 'outgoing';
  isError?: boolean;
}>`
  display: flex;
  justify-content: ${props =>
    props.type === 'incoming' ? 'flex-start' : 'flex-end'};
  margin: 16px 0;

  article {
    background-color: ${props =>
      props.isError
        ? '#fee'
        : props.type === 'incoming'
          ? '#f5f5f5'
          : '#9738FF'};
    color: ${props => (props.type === 'incoming' ? '#000' : '#fff')};
    line-height: 1.5;
    max-width: 92%;
    padding: 10px 13px;
    border-radius: 20px;
    word-break: break-word;
    position: relative;

    &:before {
      content: '';
      display: block;
      width: 36px;
      height: 26px;
      position: absolute;
      bottom: -5px;
      z-index: 1;
      ${props =>
        props.type === 'incoming'
          ? `
      left: -10px;
      border-right: 22px solid ${props.isError ? '#fee' : '#f5f5f5'};
      border-bottom-right-radius: 100%;
      transform: rotate(3deg);
                  `
          : `
      right: -10px;
      border-left: 22px solid ${props.isError ? '#fee' : '#9738FF'};
      border-bottom-left-radius: 100%;
      transform: rotate(-3deg);
                  `}
    }

    & > * {
      position: relative;
      z-index: 2;
    }

    /* WCAG: ensure links are distinguishable by more than color */
    a,
    a:visited {
      text-decoration: underline;
      text-underline-offset: 2px;
      word-break: break-word;
    }
    a:focus-visible {
      outline: 2px solid var(--color-accent-dk);
      outline-offset: 2px;
      border-radius: 2px;
    }
  }

  p {
    margin: 0 0 8px 0;
  }
  p:last-child {
    margin-bottom: 0;
  }

  blockquote {
    margin: 8px 0;
    padding: 8px 12px;
    border-left: 4px solid #ddd;
    background: #f7f7f7;
    border-radius: 6px;
  }

  code {
    background: #f0f0f0;
    padding: 2px 4px;
    border-radius: 4px;
    font-family:
      ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono',
      'Courier New', monospace;
    font-size: 0.95em;
  }

  pre code {
    display: block;
    padding: 10px 12px;
    overflow-x: auto;
  }

  table {
    width: 100%;
    border-collapse: collapse;
    margin: 8px 0;
    thead {
      tr {
        th {
          font-weight: 600;
          text-align: left;
          border-bottom: 1px solid #ddd;
          padding: 6px 8px;
        }
      }
    }
    tbody {
      tr td {
        border-bottom: 1px solid #eee;
        padding: 6px 8px;
      }
    }
  }

  /* Dark theme overrides now live in global DarkStyle under .chatMessageRoot */

  ul,
  ol {
    margin: 8px 0;
    padding-left: 20px;
    list-style-position: outside;
  }

  li {
    margin: 4px 0;
  }

  article ul {
    list-style-type: disc;
  }
  article ol {
    list-style-type: decimal;
  }
  article ul ul {
    list-style-type: circle;
  }

  header {
    display: none;
  }

  @media (max-width: 768px) {
    article {
      max-width: 100%;
    }
  }
`;

interface ChatMessageProps {
  message: {
    role: 'user' | 'assistant';
    body?: string | null;
    isError?: boolean;
  };
  messageNumber: number;
  botTitle: string;
}

// interpolation moved to helper utility

export const ChatMessage: React.FC<ChatMessageProps> = ({
  message,
  messageNumber,
  botTitle
}) => {
  const normalizeText = useCallback((text: string) => {
    return text || '';
  }, []);

  const renderMessageBody = useCallback(
    (raw?: string | null) => {
      let text = normalizeText(raw || '');
      // Convert HTML blockquotes to Markdown quotes to avoid parser edge-cases
      text = text.replace(
        /<blockquote>([\s\S]*?)<\/blockquote>/gi,
        (_m, inner) => {
          const body = String(inner)
            .replace(/^\n+|\n+$/g, '')
            .split(/\r?\n/)
            .map(line => `> ${line}`)
            .join('\n');
          return `\n${body}\n`;
        }
      );
      text = text.replace(
        /<([A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,})>/g,
        '&lt;$1&gt;'
      );
      const sanitized = DOMPurify.sanitize(text, {
        USE_PROFILES: { html: true }
      });
      return (
        <Markdown
          options={{
            forceBlock: true,
            overrides: {
              a: {
                props: { target: '_blank', rel: 'noopener noreferrer' }
              }
            }
          }}
        >
          {sanitized}
        </Markdown>
      );
    },
    [normalizeText]
  );

  const liType = message.role === 'user' ? 'outgoing' : 'incoming';
  return (
    <MessageItem type={liType} isError={message.isError} data-type={liType}>
      <article aria-labelledby={`m${messageNumber}-author`}>
        <header>
          <strong id={`m${messageNumber}-author`}>
            {message.role === 'user' ? 'You' : botTitle}
          </strong>
        </header>
        {renderMessageBody(message?.body)}
      </article>
    </MessageItem>
  );
};
